import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import type { LinkProps } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';
import type { LucideIcon } from 'lucide-react';
import {
  Eye,
  ExternalLink,
  LogIn,
  MessageCircle,
  Settings,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ReadOnlyBannerProps {
  /**
   * Which read-only audience is viewing. 'viewer' | 'commenter' are signed-in
   * roles; 'anonymous' is a signed-out visitor on a public share link, who has
   * no role at all — without this the primary audience for a public link saw a
   * fully disabled page with no explanation.
   */
  role: 'viewer' | 'commenter' | 'anonymous';
  ownerEmail: string | null | undefined;
  /** Compact presentation for embedding the notice in an existing header. */
  placement?: 'banner' | 'header';
}

/**
 * Compact `<Link>` + `<Button asChild>` inside a banner — shared shape
 * for the anonymous "Preferences" / "Sign in" pair and the "Open in
 * app" upgrade banner. Keeps the icon-label-search recipe in one
 * place so every banner action stays consistent.
 *
 * `linkProps` is spread onto the inner `<Link>` so callers pass
 * `to` / `params` / `search` directly with TanStack's full type
 * checking; we don't re-type them here to avoid drift with the
 * generated router types.
 */
function BannerActionLink({
  icon: Icon,
  label,
  variant = 'ghost',
  linkProps,
}: {
  icon: LucideIcon;
  label: string;
  variant?: 'ghost' | 'outline';
  linkProps: LinkProps;
}) {
  return (
    <Button variant={variant} size="sm" asChild>
      <Link {...linkProps} title={label}>
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {label}
      </Link>
    </Button>
  );
}

/**
 * "You have {role} access — owned by {owner}" notice for the read-only
 * `/share/...` viewer pages and for in-app surfaces gated below editor
 * (ENG-1068). Renders nothing for 'editor'/'owner' — those get the full
 * editable UI instead of this banner.
 *
 * Anonymous variant surfaces Sign-in + Preferences buttons. `?next=` is
 * stamped from the current URL (path + query + hash) so post-login the
 * auth flow lands the user back on the same share page with the same
 * DP selection / anchor (goToNextOrHome / sanitizeNextPath honor it).
 * Once authenticated, the share route re-reads `my_role` and auto-
 * forwards into the in-app workbench.
 *
 * Icon reflects the audience: Eye for viewer + anonymous, MessageCircle
 * for commenter — pairs with the workbench's read-only banner so the
 * two surfaces look like they belong to the same UX.
 */
export function ReadOnlyBanner({
  role,
  ownerEmail,
  placement = 'banner',
}: ReadOnlyBannerProps) {
  const { t } = useTranslation();
  const location = useLocation();
  // Full `next` for the Sign-in / Preferences bounce back — preserves
  // query (e.g. `?dp=42` DP selection) + hash (`#section-anchor`)
  // alongside the path so post-login the visitor lands where they
  // came from. Memoized so the Link's `search` object stays reference-
  // stable when `location` reruns for an unrelated field.
  const nextPath = useMemo(() => {
    const search = location.searchStr ?? '';
    const hash = location.hash ?? '';
    return `${location.pathname}${search}${hash}`;
  }, [location.pathname, location.searchStr, location.hash]);
  // owner_email is only exposed by the owner-only sharing-settings endpoint, so
  // a non-owner viewing a /share page has no owner to attribute — drop the
  // "owned by …" clause rather than render a placeholder. An anonymous visitor
  // can never reach that endpoint, so it has no "…Access" variant at all.
  const label =
    role === 'anonymous'
      ? t('compCommon.readOnlyBanner.anonymousAccess')
      : ownerEmail
        ? t(`compCommon.readOnlyBanner.${role}Access` as ParseKeys, {
            owner: ownerEmail,
          })
        : t(`compCommon.readOnlyBanner.${role}AccessNoOwner` as ParseKeys);
  const AudienceIcon = role === 'commenter' ? MessageCircle : Eye;

  return (
    <div
      data-slot="read-only-notice"
      className={cn(
        'flex items-center gap-2 text-sm text-muted-foreground',
        placement === 'banner' && 'border-b border-border bg-muted px-4 py-2',
        placement === 'header' && 'print:hidden',
      )}
    >
      <AudienceIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="flex-1">{label}</span>
      {role === 'anonymous' && (
        <>
          <BannerActionLink
            icon={Settings}
            label={t('compCommon.readOnlyBanner.preferences')}
            linkProps={{ to: '/preferences', search: { next: nextPath } }}
          />
          <BannerActionLink
            icon={LogIn}
            variant="outline"
            label={t('compCommon.readOnlyBanner.signInToOpenInApp')}
            linkProps={{ to: '/login', search: { next: nextPath } }}
          />
        </>
      )}
    </div>
  );
}

function OpenInAppBannerShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-2 border-b border-border bg-info/10 px-4 py-2 text-sm text-info-strong">
      <span>{t('compCommon.readOnlyBanner.openInApp')}</span>
      <Button variant="outline" size="sm" asChild>
        {children}
      </Button>
    </div>
  );
}

/**
 * Banner shown on the geometry share page to a signed-in visitor whose role
 * is 'editor'/'owner' — offers a shortcut into the real MeshGen workspace
 * instead of the read-only viewer.
 */
export function OpenGeometryInAppBanner({ uuid }: { uuid: string }) {
  const { t } = useTranslation();
  return (
    <OpenInAppBannerShell>
      <Link to="/meshgen/$uuid" params={{ uuid }}>
        <ExternalLink className="h-3.5 w-3.5" />
        {t('compCommon.readOnlyBanner.openInApp')}
      </Link>
    </OpenInAppBannerShell>
  );
}

/**
 * Banner shown on the study share page to a signed-in visitor whose role is
 * 'editor'/'owner' — offers a shortcut into the real SimOps study instead of
 * the read-only report.
 */
export function OpenStudyInAppBanner({ studyId }: { studyId: string }) {
  const { t } = useTranslation();
  return (
    <OpenInAppBannerShell>
      <Link to="/simops/$studyId" params={{ studyId }}>
        <ExternalLink className="h-3.5 w-3.5" />
        {t('compCommon.readOnlyBanner.openInApp')}
      </Link>
    </OpenInAppBannerShell>
  );
}
